import type {
  InsightAxis,
  InsightAxisType,
  ProcessedInsightAxis,
} from '@/types/insights';

export type InsightAxisAvailability = {
  hasSetupPersonalNewsOrFavoriteThemes: boolean;
  hasPersonalNewsArticles: boolean;
  hasFeaturedArticles: boolean;
};

export type InsightAxisNotice = {
  title: string;
  message: string;
  showOnboardingLink: boolean;
};

const TITLE = 'コンテンツを表示できません';
const notice = (
  message: string,
  showOnboardingLink = false,
): InsightAxisNotice => ({ title: TITLE, message, showOnboardingLink });

type Rule = {
  when: (a: InsightAxisAvailability) => boolean;
  notice: InsightAxisNotice;
};

const axisRules: Record<InsightAxisType, Rule[]> = {
  personal: [
    {
      when: a => !a.hasSetupPersonalNewsOrFavoriteThemes,
      notice: notice(
        'テーマやパーソナルニュースを設定することで、コンテンツが表示されます。',
        true,
      ),
    },
    {
      when: a => !a.hasPersonalNewsArticles,
      notice: notice(
        'パーソナルニュースおよびテーマの記事がなかったため、コンテンツを表示できませんでした。',
      ),
    },
  ],
  organization: [
    {
      when: a => !a.hasFeaturedArticles,
      notice: notice(
        '注目記事がなかったため、コンテンツを表示できませんでした。',
      ),
    },
  ],
  outside: [],
};

const emptyNotice = notice(
  '今日は表示できるコンテンツがなかったため、コンテンツを表示できませんでした。',
);

export type InsightAxisDisplay =
  | { kind: 'notice'; notice: InsightAxisNotice }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'content'; axis: ProcessedInsightAxis };

/**
 * 前提条件を満たさない軸はそもそも生成する意味が無いため、
 * error や processing より前提条件の notice を優先する。
 */
export const resolveInsightAxisDisplay = (
  axis: InsightAxis,
  availability: InsightAxisAvailability,
): InsightAxisDisplay => {
  const matched = axisRules[axis.axis].find(rule => rule.when(availability));
  if (matched) return { kind: 'notice', notice: matched.notice };
  if (axis.generation_status !== 'processed') {
    return axis.generation_status === 'processing'
      ? { kind: 'loading' }
      : { kind: 'error' };
  }
  if (axis.topics.length === 0 || axis.hints.length === 0) {
    return { kind: 'notice', notice: emptyNotice };
  }
  return { kind: 'content', axis };
};
